import { ComponentType, createContext, useContext } from 'react';
import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { panelVisible, type EventContent } from '../../contracts';
import { torontoToday, type EventDetail } from '../../lib/events';
import { stageOf } from './stages';

/** True while the viewer asked to see every tab, so panels inside the tabs show too. */
export const ShowAllTabs = createContext(false);

export const useEventContent = (eventId: string) =>
  useQuery<EventContent>(
    ['event-content', eventId],
    async () => (await api.get(`/events/${eventId}/content`)).data.data,
  );

/** Is this panel shown for the event's kind? The rule is contracts/eventPage.ts (panelVisible). */
export function usePanelShown(eventId: string, panel: string) {
  const showAll = useContext(ShowAllTabs);
  const event = useQuery<EventDetail>(['event', eventId], async () => (await api.get(`/events/${eventId}`)).data.data);
  const content = useEventContent(eventId);
  const e = event.data?.event;
  const ended = e ? stageOf(e, torontoToday()).stage === 'after' : false;
  return panelVisible(e?.kind, panel, content.data, { showAll, hybrid: e?.mode === 'hybrid', ended });
}

/** A panel that renders only where usePanelShown says so; the panel itself stays unaware of kinds. */
export function gated<P extends { eventId: string }>(panel: string, Panel: ComponentType<P>) {
  return function Gated(props: P) {
    return usePanelShown(props.eventId, panel) ? <Panel {...props} /> : null;
  };
}
